import React, { useEffect, useState } from 'react';
import { getQuizzesForCourse, getAttempts, submitQuizAttempt } from '../../lib/assess';
import type { Quiz, QuizAttempt } from '../../types/assess';
import { Stamp } from '../lms/ui';

const QuizPanel: React.FC<{ courseId: string; userId: string }> = ({ courseId, userId }) => {
  const [quizzes, setQuizzes] = useState<Quiz[]>([]);
  const [attempts, setAttempts] = useState<QuizAttempt[]>([]);
  const [answers, setAnswers] = useState<Record<string, Record<number, number>>>({});
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState('');

  const load = async () => {
    const q = await getQuizzesForCourse(courseId).catch(() => []);
    setQuizzes(q);
    const ids = q.map((x) => x.id);
    setAttempts(await getAttempts(userId, ids).catch(() => []));
  };

  // Reload when the course changes; load is intentionally scoped to that course.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { void load(); }, [courseId]);

  const submit = async (quiz: Quiz) => {
    const qs = quiz.questions ?? [];
    if (qs.length === 0) return;
    const ans = qs.map((_, i) => answers[quiz.id]?.[i] ?? -1);
    if (ans.some((a) => a < 0)) {
      setMsg('Answer all questions before submitting.');
      return;
    }
    setBusy(quiz.id);
    setMsg('');
    try {
      await submitQuizAttempt(userId, quiz.id, ans, qs, quiz.pass_mark);
      await load();
      setMsg('Attempt recorded.');
    } catch (e) {
      setMsg(e instanceof Error ? e.message : 'Submit failed');
    } finally {
      setBusy('');
    }
  };

  if (quizzes.length === 0) {
    return (
      <section>
        <h3 className="text-[14px] font-semibold text-ink">Quizzes</h3>
        <p className="mt-1 text-[12.5px] leading-5 text-ink-2">
          No quizzes have been set for this course. Your instructor will add them here.
        </p>
      </section>
    );
  }

  return (
    <section>
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <h3 className="text-[14px] font-semibold text-ink">Quizzes</h3>
        <span className="font-mono text-[9px] uppercase tracking-[0.12em] text-ink-2">
          {quizzes.length} {quizzes.length === 1 ? 'paper' : 'papers'}
        </span>
      </div>

      {msg && (
        <div className="mt-3 border border-rule bg-paper px-3 py-2 text-[12px] leading-5 text-ink" role="status">
          {msg}
        </div>
      )}

      <div className="mt-4 divide-y divide-rule border-y border-rule">
        {quizzes.map((q) => {
          const mine = attempts.filter((a) => a.quiz_id === q.id);
          const best = mine.reduce((m, a) => Math.max(m, Math.round((a.score / Math.max(a.total, 1)) * 100)), 0);
          const passed = mine.length > 0 && best >= q.pass_mark;
          return (
            <div key={q.id} className="py-5 first:pt-4 last:pb-1">
              <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
                <div className="min-w-0">
                  <p className="text-[13.5px] font-semibold leading-5 text-ink">{q.title}</p>
                  {q.description && <p className="mt-1 text-[12.5px] leading-5 text-ink-2">{q.description}</p>}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {mine.length > 0 ? (
                    <Stamp tone={passed ? 'cleared' : 'held'}>{passed ? `Passed ${best}%` : `Best ${best}%`}</Stamp>
                  ) : (
                    <Stamp tone="neutral">Not attempted</Stamp>
                  )}
                </div>
              </div>

              <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 font-mono text-[9px] uppercase tracking-[0.1em] text-ink-2">
                <span>Pass mark {q.pass_mark}%</span>
                <span aria-hidden="true">/</span>
                <span>{(q.questions ?? []).length} questions</span>
                {mine.length > 0 && (
                  <>
                    <span aria-hidden="true">/</span>
                    <span>{mine.length} {mine.length === 1 ? 'attempt' : 'attempts'}</span>
                  </>
                )}
              </div>

              <div className="mt-4 space-y-4">
                {(q.questions ?? []).map((qq, qi) => (
                  <fieldset key={qq.id}>
                    <legend className="flex gap-2 text-[12.5px] font-medium leading-5 text-ink">
                      <span className="font-mono text-[10px] text-ink-2">{String(qi + 1).padStart(2, '0')}</span>
                      <span>{qq.question}</span>
                    </legend>
                    <div className="mt-2 space-y-1.5 pl-[22px]">
                      {qq.options.map((opt, oi) => (
                        <label
                          key={oi}
                          className={`flex cursor-pointer items-start gap-2.5 border px-3 py-2 text-[12.5px] leading-5 transition-colors ${
                            answers[q.id]?.[qi] === oi
                              ? 'border-pine bg-pine-50 text-ink'
                              : 'border-rule bg-white text-ink-2 hover:bg-paper'
                          }`}
                        >
                          <input
                            type="radio"
                            name={`${q.id}-${qi}`}
                            checked={answers[q.id]?.[qi] === oi}
                            onChange={() => setAnswers({ ...answers, [q.id]: { ...(answers[q.id] || {}), [qi]: oi } })}
                            className="mt-[3px] h-3.5 w-3.5 flex-none accent-pine"
                          />
                          <span>{opt}</span>
                        </label>
                      ))}
                    </div>
                  </fieldset>
                ))}
              </div>

              <button
                onClick={() => void submit(q)}
                disabled={busy === q.id || (q.questions ?? []).length === 0}
                className="lms-btn-primary mt-4 !py-2 !text-[12px]"
              >
                {busy === q.id ? 'Submitting…' : 'Submit answers'}
              </button>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default QuizPanel;
